import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  FUEL_TYPE_LABELS,
  carLabel,
  clientDisplayName,
  INSPECTION_OVERVIEW_ANGLES,
  inspectionTemplateItemScale,
  inspectionScaleOption,
  type InspectionOverviewAngle,
  type InspectionScaleKey,
  type InspectionTone,
} from '@garage/shared'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { Spinner } from '@/ui'

const TONE_PRINT_COLOR: Record<InspectionTone, string> = {
  success: '#2f7d54',
  warning: '#9a6f14',
  error: '#c0392b',
  neutral: '#999999',
}

/**
 * Printable vehicle inspection report: same plain HTML/CSS pattern as
 * JobCardPrintView — no Tailwind, no app chrome, opened in its own tab from
 * the inspection editor. Prints every section of the checklist, not just the
 * ones with something flagged, so it reads as a complete record of what was
 * checked.
 */
export function InspectionPrintView() {
  const { id } = useParams<{ id: string }>()
  const jobcardId = id ?? ''
  const pageRef = useRef<HTMLDivElement>(null)
  const [downloading, setDownloading] = useState(false)
  const { t, i18n } = useTranslation()
  const scaleOptionLabel = (scaleKey: InspectionScaleKey, value: string, fallback: string) =>
    t(`inspection.scales.${scaleKey}.${value}`, fallback)
  const overviewAngleLabel = (angle: InspectionOverviewAngle, fallback: string) =>
    t(`inspection.overviewAngles.${angle}`, fallback)

  const { data: jobcard, isLoading: jobcardLoading } = useQuery({
    queryKey: qk.jobcard(jobcardId),
    queryFn: () => api.jobcards.get(jobcardId),
    enabled: !!jobcardId,
  })
  const { data: inspection, isLoading: inspectionLoading } = useQuery({
    queryKey: qk.inspection(jobcardId),
    queryFn: () => api.inspections.get(jobcardId),
    enabled: !!jobcardId,
  })
  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: qk.inspectionTemplate,
    queryFn: api.inspectionTemplate.tree,
  })
  const { data: settings } = useQuery({ queryKey: qk.settings, queryFn: () => api.settings.get() })

  if (jobcardLoading || inspectionLoading || templateLoading || !jobcard || !inspection || !template) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size="md" />
      </div>
    )
  }

  const car = jobcard.carSnapshot

  const handleDownloadPdf = async () => {
    if (!pageRef.current) return
    setDownloading(true)
    try {
      const html2pdf = (await import('html2pdf.js')).default
      await html2pdf()
        .set({
          filename: `inspection-${jobcard.number}.pdf`,
          margin: 0,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(pageRef.current)
        .save()
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <style>{PRINT_CSS}</style>

      <div className="no-print toolbar">
        <button className="toolbar-button" onClick={() => window.print()}>
          {t('common.print')}
        </button>
        <button className="toolbar-button" onClick={handleDownloadPdf} disabled={downloading}>
          {downloading ? t('jobCardPrint.preparingPdf') : t('jobCardPrint.downloadPdf')}
        </button>
      </div>

      <div className="sheet">
        <div className="page" ref={pageRef}>
          <header className="header">
            {settings && (
              <img
                className="logo"
                src={api.settings.logoUrl()}
                alt=""
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}
            <div className="header-text">
              <div className="business-name">{settings?.businessName || 'Cypek Garage'}</div>
              <h1 className="title">{t('inspectionPrint.vehicleInspectionReport')}</h1>
            </div>
            <div className="header-meta">
              <div>
                <span className="label">{t('inspectionPrint.jobNo')}</span> {jobcard.number}
              </div>
              <div>
                <span className="label">{t('calendar.date')}</span> {new Date(jobcard.createdAt).toLocaleDateString(i18n.language === 'ar' ? 'ar-u-nu-latn' : undefined)}
              </div>
            </div>
          </header>

          <section className="grid">
            <div className="box">
              <h2>{t('jobcardEditor.customer')}</h2>
              <div className="row">
                <span className="label">{t('jobCardPrint.name')}</span>
                <span>{jobcard.client ? clientDisplayName(jobcard.client) : '—'}</span>
              </div>
            </div>

            <div className="box">
              <h2>{t('carSelector.vehicleAriaLabel')}</h2>
              <div className="row">
                <span className="label">{t('jobCardPrint.car')}</span>
                <span>{carLabel(car)}</span>
              </div>
              <div className="row">
                <span className="label">{t('carForm.vin')}</span>
                <span>{car.vin || '—'}</span>
              </div>
              <div className="row">
                <span className="label">{t('carForm.colour')}</span>
                <span>{car.color || '—'}</span>
              </div>
              <div className="row">
                <span className="label">{t('carSelector.fuel')}</span>
                <span>{car.fuelType ? t(`carForm.fuelTypes.${car.fuelType}`, FUEL_TYPE_LABELS[car.fuelType]) : '—'}</span>
              </div>
              <div className="row">
                <span className="label">{t('jobCardPrint.odometer')}</span>
                <span>{jobcard.odometerKm != null ? `${jobcard.odometerKm} km` : '—'}</span>
              </div>
            </div>
          </section>

          <section className="box">
            <h2>{t('inspectionPrint.inspection')}</h2>
            <div className="row">
              <span className="label">{t('inspectionPrint.inspectedBy')}</span>
              <span>{inspection.performedBy || '—'}</span>
            </div>
            <div className="row">
              <span className="label">{t('calendar.date')}</span>
              <span>{inspection.performedAt || '—'}</span>
            </div>
          </section>

          {INSPECTION_OVERVIEW_ANGLES.some((a) => inspection.overviewPhotos[a.key]) && (
            <section className="box section">
              <h2>{t('inspection.vehiclePhotos')}</h2>
              <div className="overview-photos">
                {INSPECTION_OVERVIEW_ANGLES.filter((a) => inspection.overviewPhotos[a.key]).map((angle) => (
                  <div className="overview-photo" key={angle.key}>
                    <img src={api.inspections.overviewPhotoUrl(jobcardId, angle.key, 1600)} alt={overviewAngleLabel(angle.key, angle.label)} />
                    <span>{overviewAngleLabel(angle.key, angle.label)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {template.items.map((section) => {
            const sectionNote = inspection.sectionNotes[section.id]
            return (
              <section className="box section" key={section.id}>
                <h2>{section.label}</h2>
                {sectionNote && <p className="section-note">{sectionNote}</p>}
                <table className="items">
                  <tbody>
                    {section.items.map((item) => {
                      const state = inspection.items[item.id]
                      const status = state?.status
                      const scaleKey = inspectionTemplateItemScale(item)
                      const option = status
                        ? inspectionScaleOption(scaleKey, status)
                        : undefined
                      return (
                        <tr key={item.id}>
                          <td className="item-label">{item.label}</td>
                          <td className="item-status" style={{ color: TONE_PRINT_COLOR[option?.tone ?? 'neutral'] }}>
                            {option ? scaleOptionLabel(scaleKey, option.value, option.label) : '—'}
                          </td>
                          <td className="item-comment">{state?.comment || ''}</td>
                          <td className="item-photo">
                            {state?.photoPath && (
                              <img src={api.inspections.itemPhotoUrl(jobcardId, item.id, 1600)} alt={t('inspection.photoAlt', { item: item.label })} />
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </section>
            )
          })}

          {inspection.overallNotes && (
            <section className="box">
              <h2>{t('inspectionPrint.overallNotes')}</h2>
              <p className="section-note">{inspection.overallNotes}</p>
            </section>
          )}
        </div>
      </div>
    </>
  )
}

const PRINT_CSS = `
  @page {
    size: A4;
    margin: 16mm;
  }

  .sheet {
    color-scheme: light;
    background: #fff;
    min-height: 100vh;
  }

  .page {
    max-width: 190mm;
    margin: 0 auto;
    padding: 16px;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
  }

  .toolbar {
    position: fixed;
    top: 16px;
    right: 16px;
    display: flex;
    gap: 8px;
  }
  .toolbar-button {
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background: #111;
    border: none;
    border-radius: 6px;
    cursor: pointer;
  }
  .toolbar-button:hover { background: #333; }
  .toolbar-button:disabled { background: #888; cursor: wait; }

  .header {
    display: flex;
    align-items: center;
    gap: 16px;
    border-bottom: 3px solid #111;
    padding-bottom: 12px;
    margin-bottom: 16px;
  }
  .logo { height: 56px; width: auto; object-fit: contain; }
  .header-text { flex: 1; }
  .business-name { font-size: 13px; color: #555; }
  .title {
    margin: 2px 0 0;
    font-size: 22px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .header-meta { text-align: right; font-size: 13px; line-height: 1.6; white-space: nowrap; }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  .box {
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 16px;
  }
  .box h2 {
    margin: 0 0 8px;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #555;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid #eee;
    font-size: 14px;
  }
  .row:last-child { border-bottom: none; }

  .label {
    font-weight: 600;
    color: #555;
    min-width: 80px;
  }

  .section { break-inside: avoid; }
  .section-note {
    margin: 0 0 8px;
    font-size: 13px;
    color: #444;
    font-style: italic;
  }

  .items { width: 100%; border-collapse: collapse; font-size: 13px; }
  .items tr { border-bottom: 1px solid #eee; }
  .items tr:last-child { border-bottom: none; }
  .items td { padding: 5px 4px; vertical-align: top; }
  .item-label { width: 34%; }
  .item-status { width: 14%; font-weight: 700; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; }
  .item-comment { width: 36%; color: #444; }
  .item-photo { width: 16%; }
  .item-photo img { width: 44px; height: 34px; object-fit: cover; border-radius: 3px; border: 1px solid #ccc; }

  .overview-photos { display: flex; gap: 10px; flex-wrap: wrap; }
  .overview-photo { display: flex; flex-direction: column; align-items: center; gap: 4px; width: 100px; }
  .overview-photo img { width: 100px; height: 75px; object-fit: cover; border-radius: 4px; border: 1px solid #ccc; }
  .overview-photo span { font-size: 11px; color: #555; }

  @media print {
    .no-print { display: none !important; }
    .page { padding: 0; max-width: none; }
    .box { break-inside: avoid; }
  }
`
